import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MUITextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import FormHelperText from '@mui/material/FormHelperText';
import Tooltip from '@mui/material/Tooltip';
import { FormControl, InputLabel } from '@mui/material';
import ErrorBoundary from '../../components/@generalComponents/ErrorBoundary';

/**
 * @param {array} selections - Array of objects defining the columns.
 *                             Each object should have: { value: string (key), label_xx: string, type?: string, required?: boolean }
 * @param {array} value - Array of objects, where each object represents a row. Defaults to [].
 */

export const MultiColumnListItems = ({
  label,
  selections = [],
  variant = 'outlined',
  onChange,
  onBlur,
  value = [],
  size = 'small',
  keyStructure,
  help,
  fieldType,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const currentLangCode = i18n.language;
  const theme = useTheme();
  const isDarkmode = theme.palette.mode === 'dark';

  const [newRowData, setNewRowData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const initialData = selections?.reduce((acc, col) => {
      acc[col.value] = '';
      return acc;
    }, {});
    setNewRowData(initialData);
  }, [selections]);

  const currentItems = Array.isArray(value) ? value : [];

  const handleNewRowInputChange = useCallback((colValue, inputValue) => {
    setNewRowData(prev => ({ ...prev, [colValue]: inputValue }));
    if (errors[colValue]) {
      setErrors(prev => ({ ...prev, [colValue]: undefined }));
    }
  }, [errors]);

  const validateNewRow = () => {
    const newErrors = {};
    let isValid = true;
    selections.forEach(col => {
      if (col.required && !newRowData[col.value]?.trim()) {
        newErrors[col.value] = t('This field is required');
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  }

  const handleAddItem = () => {
    if (!validateNewRow()) {
      return;
    }
    const newItems = [...currentItems, { ...newRowData }];
    if (onChange) {
      onChange({ target: { name: keyStructure, value: newItems } }, keyStructure);
    }
    const initialData = selections.reduce((acc, col) => {
      acc[col.value] = '';
      return acc;
    }, {});
    setNewRowData(initialData);
    if (onBlur) { onBlur(keyStructure, newItems); }
    setErrors({});
  };

  const handleRemoveItem = (indexToRemove) => {
    const newItems = currentItems.filter((_, index) => index !== indexToRemove);
    if (onChange) {
      onChange({ target: { name: keyStructure, value: newItems } }, keyStructure);
    }
    if (onBlur) { onBlur(keyStructure, newItems); }
  };

  const canAdd = selections.some(col => col.required)
    ? selections.every(col => !col.required || !!newRowData[col.value]?.trim())
    : Object.values(newRowData).some(val => !!val?.trim());

  return (
    <ErrorBoundary>
      <div className='mb-1'>
        <FormControl fullWidth margin="normal" className="form-control-list">
          <InputLabel
            shrink={true}
            required={props?.required}
            error={props?.error}
            sx={{
              backgroundColor: isDarkmode ? 'rgb(51,51,51)' : '#FFF',

            }}
          >
            {label}
          </InputLabel>
          <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>


            <TableContainer elevation={0} component={Paper}>
              <Table size={size}>
                <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                  <TableRow>
                    {selections.map((col) => (
                      <TableCell key={col.value}>
                        {col?.[`label_${currentLangCode}`] || col.value}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ width: '50px', pr: 1 }}>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Display Existing Rows */}
                  {currentItems.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      {selections.map((col) => (
                        <TableCell key={`${rowIndex}-${col.value}`} component="th" scope="row">
                          {row[col.value]}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ pr: 1 }}>
                        <Tooltip title={t('deleteRow')} placement="left">
                          <IconButton
                            aria-label={t('deleteRow')}
                            onClick={() => handleRemoveItem(rowIndex)}
                            size="small" // Match table size
                            color="error"
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow sx={{ '& td': { borderBottom: 0 } }}>
                    {selections.map((col) => (
                      <TableCell key={`new-${col.value}`} sx={{ verticalAlign: 'top', p: 0.5 }}>
                        <MUITextField
                          variant="standard"
                          placeholder={col?.[`label_${currentLangCode}`] || col.value}
                          size={size} // Ensure consistency
                          type={col.type || 'text'}
                          value={newRowData[col.value] || ''}
                          onChange={(e) => handleNewRowInputChange(col.value, e.target.value)}
                          onBlur={onBlur}
                          name={`${keyStructure}_new_${col.value}`}
                          error={!!errors[col.value]}
                          helperText={errors[col.value]}
                          fullWidth
                          sx={{
                            '& .MuiInput-underline:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.12)' },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' },
                            '& .MuiInput-underline:after': { borderBottomWidth: '2px' },
                            '& .MuiInputBase-input': {
                              paddingTop: '4px',
                              paddingBottom: '5px',
                              marginLeft: '12px',
                              fontSize: size === 'small' ? '0.875rem' : '1rem',
                            },
                            '& .MuiFormHelperText-root': {
                              marginLeft: 0,
                              marginRight: 0,
                            }
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ verticalAlign: 'top', pt: 1, pr: 1 }}>
                      <Tooltip title={t('addRow')} placement="left">
                        <span>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddItem}
                            disabled={!canAdd}
                            size={size}
                            sx={{
                              minWidth: 'auto',
                              p: size === 'small' ? '6px' : '8px 10px',
                            }}
                            aria-label={t('addRowToList')}
                          >
                            <AddIcon fontSize={size === 'small' ? 'small' : 'medium'} />
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                </TableBody>
              </Table>
            </TableContainer>
          </div>
          {help && <FormHelperText sx={{ mx: 0.5, mb: 1 }}>{help}</FormHelperText>}
        </FormControl>
      </div>
    </ErrorBoundary>
  );
};

export default MultiColumnListItems;